<template>
  <div id="all-posts" class="posts">
    <div class="container">
      <Breadcrumb
        v-if="$route.query.search"
        class="search-breadcrumb"
        :breadcrumb="[{ name: 'Blog', slug: '/blog/' }, { name: 'Search results', slug: '' }]"
      />
      <div class="header">
        <div class="row">
          <div class="col col-3">
            <h2 class="header-title h3">
              {{ $route.query.search ? `${count} Results for "${decodeURIComponent($route.query.search)}"` : 'All Articles' }}
            </h2>
          </div>
          <div v-if="categories && categories.length && !$route.query.search" class="filters col col-9">
            <a v-if="home" :class="{ active: !$route.query.category }" @click="filter('all')">All</a>
            <a
              v-for="cat in categories" :key="cat.id"
              :class="{ active: +$route.query.category === cat.id }"
              @click="filter(cat.id)"
            >{{ cat.category }}</a>
          </div>
        </div>
      </div>
      <template v-if="articles.length">
        <transition-group name="list-complete" class="articles row" tag="div">
          <ArticleCard
            v-for="article in articles"
            :key="article.id"
            :article="article"
            class="list-complete-item col col-4"
          />
        </transition-group>
        <Pagination
          :page="+currentPage || 1"
          :total="count"
          :per-page="limit"
          align="center"
        />
      </template>
      <p v-else>
        Sorry, there were no matches for your search term. Try using a different term.
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import jsonld from '@itbpbg/lms-components/src/utils/json-ld-ds';
import global from '@itbpbg/lms-components/src/mixins/global';
import Pagination from '@itbpbg/lms-components/src/components/Pagination.vue';
import Breadcrumb from '@itbpbg/lms-components/src/components/Breadcrumb';
import ArticleCard from './ArticleCard';

export default {
  components: { ArticleCard, Pagination, Breadcrumb },
  mixins: [global],
  props: {
    page: { type: [Array, Object] },
    info: { type: [Array, Object] },
    categories: { type: Array, default: () => [], required: false },
    blogBreadcrumb: { type: [Array, Object] },
    home: { type: Boolean },
  },
  data() {
    return {
      articles: [],
      limit: 0,
      count: 0,
      filterCategories: this.$route.query.page ? [+this.$route.query.category] : [],
      currentPage: +this.$route.query.page || 1,
    };
  },
  async fetch() {
    await this.fetchAllArticles();
  },
  head() {
    let type = 'blog';
    let itemList = this.info ? this.info.categories : null;
    if (this.page.url !== 'blog' && !this.categories.length) {
      type = 'category';
      itemList = this.page.subcategories;
    } else if (this.categories && this.categories.length) {
      type = 'subcategory';
      itemList = this.results ? this.results.posts : null;
    }
    const page = {};
    let metaTitle = this.page.metaTitle || this.page.category;
    page.created = this.page.created || new Date();
    page.updated = this.page.updated || new Date();
    page.metaTitle = this.page.metaTitle || this.page.category;
    page.metaDescription = this.page.metaDescription || this.page.description;
    page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    page.metaFullImageUrl = `${this.$config.siteUrl}/resources/${this.page.metaImage || this.page.background}`;
    page.isBlog = this.page.isBlog;

    if (+this.currentPage > 1) {
      metaTitle += ` - Page ${+this.currentPage}`;
    }

    const seoWebPage = {
      ...jsonld.WebPage,
      url: `${this.$config.siteUrl}${this.$route.fullPath}`,
      name: metaTitle,
      description: page.metaDescription,
      datePublished: this.dateIsoString(page.created),
      dateModified: this.dateIsoString(page.updated),
      ...(this.$route.query.search) && { potentialAction: { ...jsonld.PotentialActions.search, target: [`${this.$config.siteUrl}${this.$route.path}?search={search_term_string}`] } },
    };
    const seoWebSite = {
      ...jsonld.WebSite,
      url: `${this.$config.siteUrl}`,
      potentialAction: { ...jsonld.PotentialActions.search, target: [`${this.$config.siteUrl}${this.$route.path}?query={search_term_string}`] },
    };

    jsonld.CollectionPage.mainEntity.itemListElement = [];
    if (itemList) {
      itemList.forEach((item) => {
        const post = {
          '@type': 'ItemPage',
          name: item.title || item.category,
          image: [`${this.$config.imagesUrl}/${item.image}`],
          description: item.description || item.metaDescription,
        };
        jsonld.CollectionPage.mainEntity.itemListElement.push(post);
      });
    }

    const breadcrumbList = type === 'blog' ? {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Blog',
          item: page.fullPath,
        },
      ],
    } : {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: jsonld.JSONBreadcrumb(this.blogBreadcrumb, this.$config.siteUrl),
    };

    const rels = this.paginationRels({ current: this.currentPage, total: this.count, perPage: this.limit }) || [];

    return {
      titleTemplate: `${metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
        ...rels,
      ],
      meta: this.metaProps({ ...page, metaTitle }),
      script: [
        {
          vmid: 'jsonld',
          type: 'application/ld+json',
          json: [
            {
              '@context': 'https://schema.org',
              '@graph': [seoWebPage, seoWebSite, jsonld.CollectionPage],
            },
            breadcrumbList,
          ],
        },
      ],
    };
  },
  watch: {
    async $route(v) {
      const page = +v.query.page || 1;
      this.currentPage = page;
      await this.fetchAllArticles();
    },
  },
  methods: {
    async fetchAllArticles() {
      const filterCategories = this.$route.query.category
        ? `filter[categoriesDeep][]=${this.$route.query.category}`
        : this.categories.map((c) => `filter[categoriesDeep][]=${c.id}`).join('&');

      const { posts, count, limit } = await this.$axios.$get(`/blog/posts?page=${this.currentPage}${filterCategories ? `&${filterCategories}` : ''}${this.$route.query.search ? `&filter[q]=${this.$route.query.search}` : ''}`).catch(() => {}) || {};
      this.articles = posts;
      this.count = count;
      this.limit = limit;
    },
    async filter(cat) {
      if (cat !== 'all') {
        this.$router.push({ query: { page: 1, category: cat } });
      } else {
        this.$router.push('');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.list-complete-item {
  transition: all 1s;
  display: inline-block;
}

.list-complete-enter,
.list-complete-leave-to
/* .list-complete-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateY(30px);
}

.list-complete-leave-active {
  position: absolute;
}

.posts {
  padding: var(--spacing-100) 0 var(--spacing-120) 0;

  .search-breadcrumb {
    margin: 0;
    margin-bottom: var(--spacing-48);
  }

  .header {
    align-items: center;
    border-bottom: 1px solid rgba(0, 12, 31, 0.12);
    padding-bottom: var(--spacing-16);

    .header-title {
      margin: 0;
    }

    .filters {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      a {
        font-weight: 500;
        line-height: 150%;
        color: var(--main-dark-shade-3);

        &:not(:last-child) {
          margin-right: var(--spacing-36);
        }

        &.active {
          color: var(--accent-shade-2);
        }
      }
    }
  }

  .articled {
    height: 1400px;
  }

  .pagination {
    margin-top: 100px;
  }
}

@include bp($bp-mobile) {
  .posts {
    .header {
      border-bottom: none;

      .header-title {
        padding-bottom: 16px;
        border-bottom: 1px solid rgba(0, 12, 31, 0.12);
        margin-bottom: var(--spacing-16);
      }

      .filters {
        display: block;
        padding-top: 12px;
        padding-bottom: 12px;

        a {
          display: block;
          padding: 12px 0;
        }
      }
    }

    .list-complete-item {
      margin-top: var(--spacing-36);
    }
  }
}
</style>
